import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useApiClient from "../hooks/useApiClient";
import { BasicMutationOptions } from "../queryClient";

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

export type PlanFeatureType =
  | "StrefaPlanistyczna"
  | "ObszarUzupelnieniaZabudowy"
  | "ObszarZabudowySrodmiejskiej"
  | "ObszarStandardowDostepnosciInfrastrukturySpolecznej";

export type PlanFeatureProperties = {
  featureType: PlanFeatureType;
  gmlId: string | null;
  localId: string | null;
  oznaczenie: string | null;
  symbol: string;
  nazwaKod: string | null;
  nazwaLabel: string | null;
  profilePodstawowy: string[] | null;
  profilePodstawowyLabels: string[] | null;
  profileDodatkowy: string[] | null;
  profileDodatkowyLabels: string[] | null;
  obowiazujeOd: string | null;
  attributes: Record<string, unknown>;
  area_m2: number | null;
};

export type PlanFeatureCollection = {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    id: number;
    geometry: GeoJSON.Geometry;
    properties: PlanFeatureProperties;
  }>;
};

export type PlanMeta = {
  uploadId: string;
  filename: string;
  fileSize: number;
  featuresCount: number;
  sourceCrs: string;
  warnings: {
    messages?: string[];
    skipped?: number;
    planMetadata?: Record<string, unknown> | null;
  } | null;
  uploadedBy: string | null;
  uploadedAt: string;
  symbolCounts: Array<{
    symbol: string;
    featureType: PlanFeatureType;
    count: number;
  }>;
};

export type PlanUploadResponse = {
  uploadId: string;
  featuresCount: number;
  sourceCrs: string;
  warnings: string[];
  skipped: number;
};

// ─────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────

/**
 * Upload GML. Multipart form-data:
 *  - `file`  — plik GML (wymagany, max 200 MB)
 *  - `sourceCrsOverride` — opcjonalny (EPSG:2176..2180). Gdy pusty, parser
 *    wykrywa CRS z `srsName` w pliku.
 */
export const useUploadPlanMutation = (
  mutationOptions?: BasicMutationOptions<PlanUploadResponse, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();

  return useMutation<
    PlanUploadResponse,
    AxiosError,
    {
      tenantId: string;
      procedureId: string;
      file: File;
      sourceCrsOverride?: string;
    }
  >({
    mutationFn: async ({ tenantId, procedureId, file, sourceCrsOverride }) => {
      const formData = new FormData();
      formData.append("file", file, file.name);
      if (sourceCrsOverride) {
        formData.append("sourceCrsOverride", sourceCrsOverride);
      }
      return (
        await privateApiClient.post(
          `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/upload`,
          formData,
        )
      ).data;
    },
    mutationKey: ["PlanUpload"],
    ...mutationOptions,
  });
};

/**
 * Fetch features w obrębie BBOX. BBOX w EPSG:2180 (PL-1992), format
 * "x1,y1,x2,y2" — przekazuje się jako string żeby axios nie parsował.
 *
 * `enabled` — flaga kontrolująca kiedy fetch leci. Domyślnie true,
 * przy pierwszym mountowaniu warstwy w OlMap można wyłączyć dopóki
 * BBOX nie jest znany (= bieżący viewport).
 */
export const useGetPlanFeaturesQuery = (
  tenantId: string,
  procedureId: string,
  params: {
    bbox?: string;
    symbol?: string;
    featureType?: string;
    detail?: "preview";
  } = {},
  options: { enabled?: boolean } = {},
) => {
  const { publicApiClient } = useApiClient();

  return useQuery<PlanFeatureCollection, AxiosError>({
    queryFn: async () =>
      (
        await publicApiClient.get(
          `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/features`,
          { params: { detail: "preview", ...params } },
        )
      ).data,
    queryKey: [
      "PlanFeatures",
      tenantId,
      procedureId,
      params.bbox ?? "",
      params.symbol ?? "",
      params.featureType ?? "",
      params.detail ?? "preview",
    ],
    enabled: options.enabled !== false && !!tenantId && !!procedureId,
    // Viewport zmienia się często — krótki staleTime, ale cache zachowujemy
    // przez 5 min (przy panoramowaniu z powrotem do wcześniejszego widoku
    // użyjemy cache'a, bez ponownego fetchu).
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
};

/** Metadane uploadu (header + count per symbol). `null` gdy plan nie istnieje. */
export const useGetPlanMetaQuery = (
  tenantId: string,
  procedureId: string,
  options: { enabled?: boolean } = {},
) => {
  const { publicApiClient } = useApiClient();

  return useQuery<PlanMeta | null, AxiosError>({
    queryFn: async () =>
      (
        await publicApiClient.get(
          `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/meta`,
        )
      ).data,
    queryKey: ["PlanMeta", tenantId, procedureId],
    enabled: options.enabled !== false && !!tenantId && !!procedureId,
    staleTime: 60_000,
  });
};

// ─────────────────────────────────────────────────────────────────────────
// Raster (COG) — MPZP
// ─────────────────────────────────────────────────────────────────────────

export type RasterMeta = {
  status: "processing" | "ready" | "failed";
  error?: string;
  s3Key?: string;
  layerName: string;
  width?: number;
  height?: number;
  bounds?: [number, number, number, number];
  cogFileSize?: number;
} | null;

export const useUploadRasterMutation = (
  mutationOptions?: BasicMutationOptions<any, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<any, AxiosError, { tenantId: string; procedureId: string; files: File[]; layerName?: string }>({
    mutationFn: async ({ tenantId, procedureId, files, layerName }) => {
      const formData = new FormData();
      for (const f of files) {
        formData.append("files", f, f.name);
      }
      if (layerName) formData.append("layerName", layerName);
      return (
        await privateApiClient.post(
          `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/upload-raster`,
          formData,
        )
      ).data;
    },
    mutationKey: ["RasterUpload"],
    ...mutationOptions,
  });
};

export const useGetRasterMetaQuery = (
  tenantId: string,
  procedureId: string,
  options: { enabled?: boolean } = {},
) => {
  const { publicApiClient } = useApiClient();
  return useQuery<RasterMeta, AxiosError>({
    queryFn: async () =>
      (
        await publicApiClient.get(
          `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/raster-meta`,
        )
      ).data,
    queryKey: ["RasterMeta", tenantId, procedureId],
    enabled: options.enabled !== false && !!tenantId && !!procedureId,
    staleTime: 60_000,
  });
};

export const useDeleteRasterMutation = (
  mutationOptions?: BasicMutationOptions<void, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();
  return useMutation<void, AxiosError, { tenantId: string; procedureId: string }>({
    mutationFn: async ({ tenantId, procedureId }) => {
      await privateApiClient.delete(
        `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}/raster`,
      );
    },
    mutationKey: ["RasterDelete"],
    ...mutationOptions,
  });
};

/** Usuwa plan (upload + features cascade). Admin-only. */
export const useDeletePlanMutation = (
  mutationOptions?: BasicMutationOptions<void, AxiosError>,
) => {
  const { privateApiClient } = useApiClient();

  return useMutation<void, AxiosError, { tenantId: string; procedureId: string }>({
    mutationFn: async ({ tenantId, procedureId }) => {
      await privateApiClient.delete(
        `/tenant/${tenantId}/plan/${encodeURIComponent(procedureId)}`,
      );
    },
    mutationKey: ["PlanDelete"],
    ...mutationOptions,
  });
};
